import React, { useState, useEffect } from 'react';
import {Box,Typography,Fade,Grow,Zoom,Container,LinearProgress,useTheme,useMediaQuery} from '@mui/material';
import {Business,Code,Speed,Security} from '@mui/icons-material';
import { GET } from '../configs/api.js';
import { setUser } from '../store/slices/userSlice.js';
import { useDispatch } from 'react-redux';
import { api } from '../utils/constants';

function AnimatedSplashScreen({setShowSplash}) {
  const [showLogo, setShowLogo] = useState(false);
  const [showTitle, setShowTitle] = useState(false);
  const [showSubtitle, setShowSubtitle] = useState(false);
  const [showFeatures, setShowFeatures] = useState(false);
  const [showProgress, setShowProgress] = useState(false);
  const [progress, setProgress] = useState(0);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const dispatch = useDispatch();
  
  
  useEffect(() => {
    const validateRefreshToken = async () => {
      const {data, status} = await GET(api.validateOnBoot);
      if (status === 200) dispatch(setUser({...data.user, accessToken: data.accessToken}));
      setShowSplash(false);
    };
    validateRefreshToken();
    
    const timeline = [
      { delay: 300, action: () => setShowLogo(true) },
      { delay: 800, action: () => setShowTitle(true) },
      { delay: 1200, action: () => setShowSubtitle(true) },
      { delay: 1600, action: () => setShowFeatures(true) },
      { delay: 2000, action: () => setShowProgress(true) }
    ];

    timeline.forEach(({ delay, action }) => {
      setTimeout(action, delay);
    });

    // Progress bar animation
    setTimeout(() => {
      const progressInterval = setInterval(() => {
        setProgress(prev => {
          if (prev >= 100) {
            clearInterval(progressInterval);
            return 100;
          }
          return prev + 2;
        });
      }, 50);
    }, 2000);
  }, [dispatch, setShowSplash]);

  const features = [
    { icon: Business, text: 'Business Management', delay: 0 },
    { icon: Code, text: 'Modern Technology', delay: 200 },
    { icon: Speed, text: 'Fast Performance', delay: 400 },
    { icon: Security, text: 'Secure Platform', delay: 600 }
  ];

  return (
    <Fade in={true} timeout={500}>
      <Container maxWidth="lg">
        <Box
          sx={{minHeight: '100vh',display: 'flex',flexDirection: 'column',alignItems: 'center',justifyContent: 'center',background: 'linear-gradient(135deg, #000000 0%, #1a1a1a 50%, #0a0a0a 100%)',position: 'fixed',top: 0,left: 0,right: 0,bottom: 0,zIndex: 9999,overflow: 'hidden',px: 2}}
        >
        {/* Animated background elements */}
        <Box
          sx={{position: 'absolute',top: '10%',left: '10%',width: 100,height: 100,borderRadius: '50%',background: 'radial-gradient(circle, rgba(255, 255, 255, 0.12) 0%, rgba(255, 255, 255, 0.04) 100%)',animation: 'float 6s ease-in-out infinite',animationDelay: '0s',filter: 'blur(1px)'}}
        />
        <Box
          sx={{position: 'absolute',top: '60%',right: '15%',width: 60,height: 60,borderRadius: '50%',background: 'radial-gradient(circle, rgba(255, 255, 255, 0.1) 0%, rgba(255, 255, 255, 0.03) 100%)',animation: 'float 8s ease-in-out infinite',animationDelay: '2s',filter: 'blur(1px)'}}
        />
        <Box
          sx={{position: 'absolute',bottom: '20%',left: '20%',width: 80,height: 80,borderRadius: '50%',background: 'radial-gradient(circle, rgba(255, 255, 255, 0.08) 0%, rgba(255, 255, 255, 0.02) 100%)',animation: 'float 7s ease-in-out infinite',animationDelay: '4s',filter: 'blur(1px)'
          }}
        />

        {/* Main content */}
        <Box
          sx={{textAlign: 'center',color: 'white',zIndex: 1,maxWidth: 600,width: '100%'
          }}
        >
          {/* Logo */}
          <Zoom in={showLogo} timeout={800}>
            <Box
              sx={{width: isMobile ? 100 : 120,height: isMobile ? 100 : 120,borderRadius: '50%',background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.2) 0%, rgba(255, 255, 255, 0.1) 100%)',backdropFilter: 'blur(20px)',display: 'flex',alignItems: 'center',justifyContent: 'center',margin: '0 auto 32px auto',boxShadow: '0 20px 60px rgba(0, 0, 0, 0.5), inset 0 0 20px rgba(255, 255, 255, 0.1)',animation: 'pulse 3s ease-in-out infinite',border: '2px solid rgba(255, 255, 255, 0.3)',transition: 'all 0.3s ease'
              }}
            >
              <Business sx={{ fontSize: isMobile ? 50 : 60, color: 'white', filter: 'drop-shadow(0 2px 8px rgba(0, 0, 0, 0.3))' }} />
            </Box>
          </Zoom>

          {/* Title */}
          <Fade in={showTitle} timeout={1000}>
            <Typography
              variant={isMobile ? 'h3' : 'h2'}
              component="h1"
              gutterBottom
              sx={{fontWeight: 800,background: 'linear-gradient(135deg, #ffffff 0%, #e0e0e0 50%, #ffffff 100%)',backgroundClip: 'text',WebkitBackgroundClip: 'text',WebkitTextFillColor: 'transparent',textShadow: '0 4px 20px rgba(0, 0, 0, 0.5)',mb: 2,letterSpacing: '-0.02em',backgroundSize: '200% auto',animation: 'shimmer 3s linear infinite'
              }}
            >
              Contracting Pro
            </Typography>
          </Fade>

          {/* Subtitle */}
          <Grow in={showSubtitle} timeout={1000}>
            <Typography variant={isMobile ? 'h6' : 'h5'}component="h2"sx={{  color: 'rgba(255, 255, 255, 0.9)',  fontWeight: 300,  mb: 6,  lineHeight: 1.4,  textShadow: '0 2px 10px rgba(0, 0, 0, 0.2)'}}>
              Streamline your business operations with our comprehensive
              {!isMobile && <br />}
              contracting management platform
            </Typography>
          </Grow>

          {/* Features */}
          {showFeatures && (
            <Box
              sx={{display: 'grid',gridTemplateColumns: isMobile ? '1fr 1fr' : 'repeat(4, 1fr)',gap: isMobile ? 3 : 4,mb: 6,mt: 4}}
            >
              {features.map((feature, index) => (
                <Fade key={index} in timeout={1000} style={{ transitionDelay: `${feature.delay}ms` }}>
                  <Box
                    sx={{display: 'flex',flexDirection: 'column',alignItems: 'center',padding: isMobile ? 2 : 3,borderRadius: 3,background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.12) 0%, rgba(255, 255, 255, 0.08) 100%)',backdropFilter: 'blur(20px)',border: '1px solid rgba(255, 255, 255, 0.25)',transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)','&:hover': {  transform: 'translateY(-8px) scale(1.02)',  background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.18) 0%, rgba(255, 255, 255, 0.12) 100%)',  boxShadow: '0 15px 40px rgba(0, 0, 0, 0.4), inset 0 0 20px rgba(255, 255, 255, 0.1)',  border: '1px solid rgba(255, 255, 255, 0.35)'}}}
                  >
                    <feature.icon sx={{ fontSize: isMobile ? 32 : 40, color: 'white', mb: 1,filter: 'drop-shadow(0 2px 8px rgba(0, 0, 0, 0.3))',transition: 'transform 0.3s ease','&:hover': { transform: 'scale(1.1)' }}} 
                    />
                    <Typography
                      variant={isMobile ? 'body2' : 'body1'}
                      sx={{color: 'rgba(255, 255, 255, 0.95)',fontWeight: 500,textAlign: 'center',fontSize: isMobile ? '0.8rem' : '0.9rem'}}>
                      {feature.text}
                    </Typography>
                  </Box>
                </Fade>
              ))}
            </Box>
          )}

          {/* Progress Bar */}
          <Fade in={showProgress} timeout={1000}>
            <Box sx={{ width: '100%', maxWidth: 400, mx: 'auto', mt: 4 }}>
              <Typography variant="body2" sx={{   color: 'rgba(255, 255, 255, 0.8)',   mb: 2,   fontWeight: 500 }}>
                Loading your workspace...
              </Typography>
              <Box
                sx={{background: 'rgba(0, 0, 0, 0.3)',borderRadius: 2,overflow: 'hidden',height: 8,mb: 1,border: '1px solid rgba(255, 255, 255, 0.1)'}}
              >
                <LinearProgress variant="determinate" value={progress} sx={{   height: '100%',   backgroundColor: 'transparent',   '& .MuiLinearProgress-bar': {     background: 'linear-gradient(90deg, #ffffff 0%, #e0e0e0 50%, #ffffff 100%)',     backgroundSize: '200% 100%',     animation: 'progressShimmer 2s linear infinite',     borderRadius: 2,     boxShadow: '0 0 10px rgba(255, 255, 255, 0.3)'   } }}/>
              </Box>
              <Typography
                variant="caption"
                sx={{
                  color: 'rgba(255, 255, 255, 0.7)',
                  fontSize: '0.75rem'
                }}
              >
                {progress}%
              </Typography>
            </Box>
          </Fade>
        </Box>

        {/* CSS Animations */}
        <style>
          {`
            @keyframes float {
              0%, 100% {
                transform: translateY(0px) translateX(0px);
              }
              33% {
                transform: translateY(-20px) translateX(10px);
              }
              66% {
                transform: translateY(-10px) translateX(-10px);
              }
            }

            @keyframes pulse {
              0% {
                transform: scale(1);
                box-shadow: 0 20px 60px rgba(0, 0, 0, 0.5), inset 0 0 20px rgba(255, 255, 255, 0.1);
              }
              50% {
                transform: scale(1.05);
                box-shadow: 0 25px 80px rgba(0, 0, 0, 0.6), inset 0 0 30px rgba(255, 255, 255, 0.15);
              }
              100% {
                transform: scale(1);
                box-shadow: 0 20px 60px rgba(0, 0, 0, 0.5), inset 0 0 20px rgba(255, 255, 255, 0.1);
              }
            }

            @keyframes shimmer {
              0% {
                background-position: 0% center;
              }
              100% {
                background-position: 200% center;
              }
            }

            @keyframes progressShimmer {
              0% {
                background-position: 0% center;
              }
              100% {
                background-position: 200% center;
              }
            }
          `}
        </style>
        </Box>
      </Container>
    </Fade>
  );
}

export default AnimatedSplashScreen;